export { StoreProvider, createMfeStore } from './chunk-SEDIOAWQ.js';
import { useSelector, useDispatch } from 'react-redux';
import { createApi } from '@reduxjs/toolkit/query/react';
import { getContextClient } from '@quickbee/api-client';
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { platformEvents } from '@quickbee/events';
import { createSlice } from '@reduxjs/toolkit';

function createTypedHooks() {
  return {
    useAppDispatch: () => useDispatch(),
    useAppSelector: useSelector
  };
}

// src/api/types.ts
function isApiClientErrorLike(error) {
  return typeof error === "object" && error !== null && "message" in error && typeof error.message === "string";
}

// src/api/baseQuery.ts
function normalizeError(error) {
  if (isApiClientErrorLike(error)) {
    return {
      status: error.status,
      code: error.code,
      message: error.message,
      field: error.field,
      details: error.details,
      retryAfterSeconds: error.retryAfterSeconds
    };
  }
  if (error instanceof Error) {
    return { message: error.message };
  }
  return { message: "Unknown error" };
}
function createDomainBaseQuery(client) {
  return async (args, api) => {
    const requestArgs = typeof args === "string" ? { url: args } : args;
    try {
      const data = await client.request({
        ...requestArgs,
        method: requestArgs.method ?? "GET",
        ...api?.signal ? { signal: api.signal } : {}
      });
      return { data: data ?? null };
    } catch (error) {
      return { error: normalizeError(error) };
    }
  };
}
function createDomainApi(config) {
  const { client, ...createApiOptions } = config;
  return createApi({
    // The applied half of `CACHE_POLICY`'s refetch stance. Without an api-level
    // value RTK never refetches on remount, so navigating between MFE pages
    // inside `keepUnusedDataFor` (60–900 s) renders cached data and fires no
    // request at all — a finance app showing minutes-old balances. `true`
    // rather than a staleness window: a window makes the same menu click
    // sometimes fire a request and sometimes not, which reads as a defect and
    // is impossible to reason about while testing. Cached data still paints
    // instantly; the refetch revalidates behind it. Safe for
    // cursor-accumulating endpoints because components hold cursors in local
    // state (a remount re-subscribes with page-1 args) and
    // `createCursorPagedEndpoint.merge` replaces, not appends, on a cursor-less
    // refetch. Callers can still override per-api or per-hook.
    refetchOnMountOrArgChange: true,
    ...createApiOptions,
    baseQuery: createDomainBaseQuery(client)
  });
}
function createContextApi(config) {
  const { context, publicClient, ...domainApiConfig } = config;
  return createDomainApi({
    ...domainApiConfig,
    client: getContextClient({
      context,
      ...publicClient ? { publicClient: true } : {}
    })
  });
}

// src/api/cursorPagination.ts
function stableStringify(value) {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const entries = Object.entries(value).filter(([, v]) => v !== void 0).sort(([a], [b]) => a.localeCompare(b));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(",")}}`;
}
function createCursorPagedEndpoint() {
  return {
    serializeQueryArgs: ({ queryArgs, endpointName }) => {
      const { cursor: _cursor, ...rest } = queryArgs ?? {};
      return `${endpointName}(${stableStringify(rest)})`;
    },
    merge: (currentCache, incoming, { arg }) => {
      if (!arg?.cursor) {
        currentCache.items = incoming.items;
        currentCache.pagination = incoming.pagination;
        return;
      }
      currentCache.items.push(...incoming.items);
      currentCache.pagination = incoming.pagination;
    },
    forceRefetch: ({
      currentArg,
      previousArg
    }) => currentArg?.cursor !== previousArg?.cursor
  };
}
function emptyPage() {
  return { items: [], pagination: { cursor: null, hasMore: false, total: 0 } };
}
function nextPageArgs(args, result) {
  if (!result?.pagination.hasMore || !result.pagination.cursor) return null;
  return { ...args, cursor: result.pagination.cursor };
}
function useCursorPager({
  pageSize,
  resetKey,
  onPageSizeChange
}) {
  const [cursors, setCursors] = useState([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [targetPageIndex, setTargetPageIndex] = useState(0);
  const latest = useRef({ result: void 0, isFetching: false, spent: false });
  const observe = useCallback((result, isFetching) => {
    const current = latest.current;
    if (current.result === result && current.isFetching === isFetching) return;
    latest.current = { result, isFetching, spent: false };
  }, []);
  useEffect(() => {
    latest.current.spent = true;
    setCursors([null]);
    setPageIndex(0);
    setTargetPageIndex(0);
  }, [resetKey, pageSize]);
  useEffect(() => {
    const current = latest.current;
    if (targetPageIndex <= pageIndex || current.isFetching || !current.result || current.spent) {
      return;
    }
    const next = current.result.pagination.cursor;
    if (!current.result.pagination.hasMore || !next) {
      setTargetPageIndex(pageIndex);
      return;
    }
    current.spent = true;
    setCursors(
      (walked) => walked[pageIndex + 1] !== void 0 ? walked : [...walked.slice(0, pageIndex + 1), next]
    );
    setPageIndex((index) => index + 1);
  });
  const goToPage = useCallback(
    (oneBasedPage, nextPageSize) => {
      if (nextPageSize !== pageSize && onPageSizeChange) {
        onPageSizeChange(nextPageSize);
        return;
      }
      const target = Math.max(0, oneBasedPage - 1);
      setTargetPageIndex(target);
      setPageIndex((current) => target < cursors.length ? target : current);
    },
    [cursors.length, pageSize, onPageSizeChange]
  );
  const cursor = cursors[pageIndex] ?? null;
  return useMemo(
    () => ({
      cursor,
      page: pageIndex + 1,
      goToPage,
      observe,
      isWalking: targetPageIndex > pageIndex
    }),
    [cursor, pageIndex, goToPage, observe, targetPageIndex]
  );
}
function pagerTotalCount(pagination, pageIndex, pageSize, loadedRows) {
  if (!pagination) return loadedRows;
  if (pagination.total !== null && pagination.total >= 0) return pagination.total;
  return pageIndex * pageSize + loadedRows + (pagination.hasMore ? 1 : 0);
}

// src/api/tags.ts
var LIST_ID = "LIST";
function providesList(type, result, idKey = "id") {
  const items = Array.isArray(result) ? result : result?.items ?? [];
  const tags = [{ type, id: LIST_ID }];
  for (const item of items) {
    const id = item?.[idKey];
    if (typeof id === "string" || typeof id === "number") {
      tags.push({ type, id });
    }
  }
  return tags;
}
function providesOne(type, id) {
  return id === void 0 ? [{ type, id: LIST_ID }] : [{ type, id }];
}
function invalidatesOne(type, id) {
  return id === void 0 ? [{ type, id: LIST_ID }] : [{ type, id }, { type, id: LIST_ID }];
}

// src/api/cachePolicy.ts
var CACHE_POLICY = {
  /** Live gold rate. Stale prices must never be transacted on (`PRICE_STALE`, §8.3). */
  livePricing: {
    keepUnusedDataFor: 15,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
    pollingIntervalMs: 3e4
  },
  /** Ledger accounts, redeemable balances — money the user is looking at. */
  financialBalance: {
    keepUnusedDataFor: 60,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true
  },
  /** Tickets, disputes, loan leads, orders. */
  transactionalList: {
    keepUnusedDataFor: 120,
    refetchOnMountOrArgChange: true
  },
  /** Scheme catalogue, templates, feature flags — changes rarely, read constantly. */
  reference: {
    keepUnusedDataFor: 900,
    refetchOnMountOrArgChange: false
  },
  /** Generated reports and dashboards — expensive, explicitly refreshed. */
  report: {
    keepUnusedDataFor: 300,
    refetchOnMountOrArgChange: false
  },
  /**
   * Integration health projections (pricing feed-status). A minute, not 30 s:
   * the projection itself moves at the feed's ~5-minute tick cadence, so faster
   * polling reads the same row again. Applied by the hook caller, gated to the
   * visible page (`pollingInterval: active ? … : 0`); gateway *config* reads
   * deliberately have no polling policy — a config changes when an operator
   * changes it.
   */
  feedHealth: {
    keepUnusedDataFor: 30,
    pollingIntervalMs: 6e4
  }
};
var DEFAULT_SETTLE_DELAY_MS = 800;
function registerCrossContextInvalidation(options) {
  const { store, api, rules } = options;
  const timers = /* @__PURE__ */ new Set();
  const unsubscribes = [];
  for (const rule of rules) {
    const unsubscribe = platformEvents.on(rule.event, (payload) => {
      const tags = rule.invalidate(payload);
      if (tags.length === 0) return;
      const delay = rule.settleDelayMs ?? DEFAULT_SETTLE_DELAY_MS;
      const timer = setTimeout(() => {
        timers.delete(timer);
        store.dispatch(api.util.invalidateTags(tags));
      }, delay);
      timers.add(timer);
    });
    unsubscribes.push(unsubscribe);
  }
  return () => {
    unsubscribes.forEach((dispose) => dispose());
    timers.forEach((timer) => clearTimeout(timer));
    timers.clear();
  };
}
function emitPlatformEvent(event, payload) {
  platformEvents.emit(event, payload);
}
function resetApiState(store, apis) {
  for (const api of apis) {
    store.dispatch(api.util.resetApiState());
  }
}
function principalKey(user) {
  if (typeof user !== "object" || user === null) return null;
  const { userId, id, sub, tenantId } = user;
  const who = [userId, id, sub].find((v) => typeof v === "string" && v !== "") ?? null;
  if (!who) return null;
  return `${who}@${typeof tenantId === "string" ? tenantId : ""}`;
}
function bindAuthStateReset(store, apis) {
  const reset = () => resetApiState(store, apis);
  let lastKey;
  const disposers = [
    platformEvents.on("authStateChanged", ({ user, isAuthenticated }) => {
      if (!isAuthenticated) {
        lastKey = null;
        reset();
        return;
      }
      const key = principalKey(user);
      const rotation = key !== null && (lastKey === void 0 || key === lastKey);
      lastKey = key;
      if (!rotation) reset();
    }),
    platformEvents.on("sessionExpired", reset)
  ];
  return () => disposers.forEach((dispose) => dispose());
}
function createDrawerSlice(name, options = {}) {
  const initialState = {
    isOpen: false,
    selectedId: options.initialSelectedId ?? null
  };
  return createSlice({
    name,
    initialState,
    reducers: {
      open: (state, action) => {
        state.isOpen = true;
        if (action.payload !== void 0) {
          state.selectedId = action.payload;
        }
      },
      close: (state) => {
        state.isOpen = false;
        state.selectedId = null;
      }
    }
  });
}
function createDraftSlice(name, initialDraft) {
  const initialState = {
    draft: initialDraft,
    loading: false
  };
  return createSlice({
    name,
    initialState,
    reducers: {
      setDraft: (state, action) => {
        state.draft = { ...state.draft, ...action.payload };
      },
      resetDraft: (state) => {
        state.draft = initialDraft;
      },
      setLoading: (state, action) => {
        state.loading = action.payload;
      }
    }
  });
}

export { CACHE_POLICY, DEFAULT_SETTLE_DELAY_MS, LIST_ID, bindAuthStateReset, createContextApi, createCursorPagedEndpoint, createDomainApi, createDomainBaseQuery, createDraftSlice, createDrawerSlice, createTypedHooks, emitPlatformEvent, emptyPage, invalidatesOne, isApiClientErrorLike, nextPageArgs, pagerTotalCount, providesList, providesOne, registerCrossContextInvalidation, resetApiState, useCursorPager };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map